"use client";
import { useMemoizedFn } from 'ahooks';
import React, { ComponentType, FC, useEffect, useRef, useState } from 'react'
import log from "logbeacon";
import { cancelIdleCallbackCompat, requestIdleCallbackCompat } from '../requestIdle';
import { THydrateOptions, ComponentProps, AnyFunction, LAZY_HYDRATE_MARKER } from './types';

const defaultIdleCallbackTimeout = 3000
const defaultSetTimeoutDelay = 1500
const defaultRootMargin = '100px 0px'

export const hydrateFunction = <P extends ComponentProps>(
  Component: ComponentType<Omit<P, 'wrapperProps'>>,
  options: THydrateOptions,
): FC<P> => {
  const {
    observerOptions,
    idleOptions,
  } = options

  const {
    rootMargin = defaultRootMargin,
    wrapperElement,
    enableObserverVisible = false,
  } = observerOptions ?? {}
  const {
    idleCallbackTimeout = defaultIdleCallbackTimeout,
    setTimeoutDelay = defaultSetTimeoutDelay,
    enableIdleCallback = false,
  } = idleOptions ?? {}

  const Hydration: FC<P & { forceHydrate?: boolean }> = ({ wrapperProps = {}, forceHydrate = false, ...props }) => {
    const rootRef = useRef<HTMLDivElement>(null)
    const [isHydrated, setIsHydrated] = useState(false)
    const cleanupFunctions = useRef<AnyFunction[]>([])

    /**
     * 清理所有已注册的清理函数
     */
    const cleanUp = useMemoizedFn(() => {
      cleanupFunctions.current.forEach(fn => fn())
      cleanupFunctions.current = []
    })

    /**
     * 执行水合操作
     */
    const hydrate = useMemoizedFn(() => {
      cleanUp()
      if (isHydrated) {
        return
      }
      setIsHydrated(true)
    })

    /**
     * 使用 requestIdleCallback 延迟水合
     * 在浏览器空闲时执行水合操作，避免阻塞主线程
     * 与 IntersectionObserver 并行竞争，谁先触发谁执行
     */
    const initIdleCallback = useMemoizedFn(() => {
      const idleCallbackHandle = requestIdleCallbackCompat(
        () => {
          // 在下一帧执行水合
          requestAnimationFrame(() => {
            const cleartId = setTimeout(() => {
              hydrate()
            }, setTimeoutDelay)
            cleanupFunctions.current.push(() => {
              clearTimeout(cleartId)
            })
          })
        },
        {
          timeout: idleCallbackTimeout,
        },
      )

      // 注册清理函数
      cleanupFunctions.current.push(() => {
        cancelIdleCallbackCompat(idleCallbackHandle)
      })
    })

    /**
     * 初始化 IntersectionObserver
     * 监听组件是否进入视口
     * 与 requestIdleCallback 并行竞争，谁先触发谁执行
     */
    const initIntersectionObserver = useMemoizedFn(() => {
      const observerOptions = {
        rootMargin,
        threshold: 0,
        root: null,
      }

      // 如果不支持 IntersectionObserver 或者没有 ref，直接水合
      const shouldHydrate = !('IntersectionObserver' in window) || !rootRef.current

      if (shouldHydrate) {
        hydrate()
        return
      }

      // 创建观察器
      const observer = new IntersectionObserver(([entry]) => {
        if (entry.isIntersecting || entry.intersectionRatio > 0) {
          // 组件进入视口，直接触发水合（会自动 cleanUp 其他监听器）
          hydrate()
        }
      }, observerOptions)

      observer.observe(rootRef.current!)

      // 注册清理函数
      cleanupFunctions.current.push(() => {
        observer.disconnect()
      })
    })

    /**
     * 并行初始化所有事件监听器
     * 参考：on.forEach(event => initEvent(event))
     * 任何一个触发后，hydrate() 会调用 cleanUp() 清理所有其他监听器
     */
    useEffect(() => {
      if (isHydrated) {
        return cleanUp
      }

      // 1. 如果启用 IdleCallback，添加空闲时水合监听
      if (enableIdleCallback) {
        initIdleCallback()
      }
      if (enableObserverVisible) {
        initIntersectionObserver()
      }

      // 返回清理函数
      return cleanUp
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isHydrated])

    useEffect(() => {
      log.debug('[文案]forceHydrate-start', { forceHydrate, isHydrated })
      if (isHydrated) {
        return cleanUp
      }
      if (forceHydrate) {
        log.debug('[文案]forceHydrate-end-forceHydrate-true', { forceHydrate, isHydrated })
        hydrate()
        return cleanUp
      }
      log.debug('[文案]forceHydrate-end-forceHydrate-false', { forceHydrate, isHydrated })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [forceHydrate])


    if (isHydrated) {
      return (
        <Component {...(props as Omit<P, 'wrapperProps'>)} />
      )
    }
    if (!wrapperElement) {
      return undefined
    }
    const WrapperElement = wrapperElement as unknown as FC<React.HTMLProps<HTMLElement>>

    return (
      <WrapperElement
        ref={rootRef}
        {...wrapperProps}
        dangerouslySetInnerHTML={{ __html: '' }}
        suppressHydrationWarning
      />
    )
  }

  (Hydration as unknown as Record<symbol, unknown>)[LAZY_HYDRATE_MARKER] = true

  return Hydration
}
